import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig } from './FinancialConfig.js';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';
import CalculationTransparency from './components/CalculationTransparency.js';

const SMSFCalculatorWizard = ({ smsfState, setSmsfState, onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [wizardData, setWizardData] = useState({
        // Step 1: Welcome (no data, just intro)
        welcome: {},
        // Step 2: Fund Basics
        fundBasics: {
            fundName: '',
            abn: '',
            tfn: ''
        },
        // Step 3: Trustees
        trustees: [
            { name: '', tfn: '', dob: '' },
            { name: '', tfn: '', dob: '' }
        ],
        // Step 4: Members (links to trustees)
        members: [
            { name: '', age: 0 },
            { name: '', age: 0 }
        ],
        // Step 5: Initial Balances
        initialBalances: {
            member1Balance: 0,
            member2Balance: 0
        },
        // Step 6: Bank Details
        bankDetails: {
            bsb: '',
            accountNumber: '',
            accountName: ''
        },
        // Step 7: Investment Strategy
        investmentStrategy: {
            riskProfile: 'balanced', // conservative, balanced, growth
            australianShares: 30,
            internationalShares: 25,
            property: 10,
            bonds: 20,
            cash: 15
        }
    });

    const [validation, setValidation] = useState({});
    const [recommendations, setRecommendations] = useState([]);

    const steps = [
        {
            id: 'welcome',
            title: 'Welcome',
            icon: '👋',
            description: 'Get started with setup'
        },
        {
            id: 'fund_basics',
            title: 'Fund Details',
            icon: '🏛️',
            description: 'Fund name and ABN'
        },
        {
            id: 'members',
            title: 'Trustees',
            icon: '👥',
            description: 'Add trustees'
        },
        {
            id: 'members_balances',
            title: 'Members',
            icon: '💼',
            description: 'Member balances'
        },
        {
            id: 'initial_balances',
            title: 'Initial Balances',
            icon: '💵',
            description: 'Starting super'
        },
        {
            id: 'bank_details',
            title: 'Bank Details',
            icon: '🏦',
            description: 'BSB and account number'
        },
        {
            id: 'investment_strategy',
            title: 'Investment Strategy',
            icon: '📊',
            description: 'Risk profile and allocation'
        },
        {
            id: 'review',
            title: 'Complete',
            icon: '✓',
            description: 'Summary and next steps'
        }
    ];

    // Validate current step
    const validateStep = (step) => {
        const errors = {};

        switch(step) {
            case 0: // Welcome - no validation
                break;

            case 1: // Fund Basics
                if (!wizardData.fundBasics.fundName) errors.fundName = 'Fund name is required';
                if (!wizardData.fundBasics.abn || wizardData.fundBasics.abn.length !== 11) {
                    errors.abn = 'Valid 11-digit ABN is required';
                }
                break;

            case 2: // Trustees
                if (!wizardData.trustees[0]?.name) errors.trustee1 = 'At least one trustee name required';
                break;

            case 3: // Members
                // Optional validation
                break;

            case 4: // Initial Balances
                // Optional validation
                break;

            case 5: // Bank Details
                if (wizardData.bankDetails.bsb && wizardData.bankDetails.bsb.length !== 6) {
                    errors.bsb = 'BSB must be 6 digits';
                }
                break;

            case 6: // Investment Strategy
                const total = wizardData.investmentStrategy.australianShares + 
                             wizardData.investmentStrategy.internationalShares +
                             wizardData.investmentStrategy.property +
                             wizardData.investmentStrategy.bonds +
                             wizardData.investmentStrategy.cash;
                if (Math.abs(total - 100) > 0.1) {
                    errors.allocation = 'Asset allocation must total 100%';
                }
                break;

            case 7: // Review - no validation
                break;
        }

        setValidation(errors);
        return Object.keys(errors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            if (currentStep === steps.length - 2) {
                // Generate recommendations before final step
                generateRecommendations();
            }
            setCurrentStep(Math.min(currentStep + 1, steps.length - 1));
        }
    };

    const prevStep = () => {
        setCurrentStep(Math.max(currentStep - 1, 0));
    };

    const generateRecommendations = () => {
        const recs = [];
        const totalBalance = (wizardData.initialBalances?.member1Balance || 0) + (wizardData.initialBalances?.member2Balance || 0);

        // Asset allocation analysis
        const strategy = wizardData.investmentStrategy || {};
        const equityExposure = (strategy.australianShares || 0) + (strategy.internationalShares || 0);
        const propertyExposure = strategy.property || 0;
        const cashExposure = strategy.cash || 0;

        // Based on risk profile
        const targetAllocations = {
            conservative: { equity: 35, property: 5, fixed: 40, cash: 20 },
            balanced: { equity: 55, property: 10, fixed: 20, cash: 15 },
            growth: { equity: 75, property: 15, fixed: 5, cash: 5 }
        };

        const target = targetAllocations[strategy.riskProfile || 'balanced'];

        if (Math.abs(equityExposure - target.equity) > 15) {
            recs.push({
                category: 'Asset Allocation',
                priority: 'high',
                title: 'Equity allocation differs from profile',
                message: `Current equity: ${equityExposure.toFixed(0)}%, Target: ${target.equity}%`,
                action: equityExposure < target.equity ? 'Consider increasing equity exposure' : 'Consider reducing equity exposure'
            });
        }

        if (cashExposure > 20) {
            recs.push({
                category: 'Asset Allocation',
                priority: 'medium',
                title: 'High cash allocation',
                message: `${cashExposure.toFixed(0)}% in cash - may reduce long-term returns`,
                action: 'Consider deploying cash into growth assets'
            });
        }

        // General recommendations based on profile
        if (totalBalance > 0) {
            const estimatedFees = calculateEstimatedFees(totalBalance, wizardData.fundBasics?.trusteeType || 'individual');
            const feePercentage = totalBalance > 0 ? (estimatedFees / totalBalance) * 100 : 0;
            
            if (feePercentage > 1.5) {
                recs.push({
                    category: 'Fees',
                    priority: 'medium',
                    title: 'Review fee structure',
                    message: `Estimated fees: ${feePercentage.toFixed(2)}% of assets`,
                    action: 'Compare service providers for potential savings'
                });
            }
        }

        // Age-based recommendations
        const members = wizardData.members || [];
        const ages = members.map(m => m.age || 0).filter(a => a > 0);
        if (ages.length > 0) {
            const youngestAge = Math.min(...ages);
            const oldestAge = Math.max(...ages);
            
            if (youngestAge < 60 && youngestAge > 0) {
                recs.push({
                    category: 'Insurance',
                    priority: 'medium',
                    title: 'Consider insurance protection',
                    message: 'Members under 60 may benefit from insurance within SMSF',
                    action: 'Explore Life, TPD, and Income Protection options'
                });
            }

            if (oldestAge >= 60) {
                recs.push({
                    category: 'Tax Optimization',
                    priority: 'high',
                    title: 'Pension phase available',
                    message: 'Members 60+ can access tax-free pension',
                    action: 'Consider transitioning to pension phase for 0% tax on earnings'
                });
            }
        }

        // Balance-based recommendations
        if (totalBalance > 200000) {
            recs.push({
                category: 'Growth Strategy',
                priority: 'low',
                title: 'LRBA opportunity',
                message: 'Fund size allows for property investment',
                action: 'Consider LRBA for property exposure and leverage'
            });
        }

        setRecommendations(recs);
    };

    const calculateEstimatedFees = (totalAssets, trusteeType) => {
        const trusteeFee = trusteeType === 'corporate' ? 2000 : 700;
        const auditFee = 1500;
        const complianceFee = 2100;
        const accountingFee = 1800;
        
        return trusteeFee + auditFee + complianceFee + accountingFee;
    };

    const completeWizard = () => {
        // Save all data to smsfState
        const totalBalance = (wizardData.initialBalances?.member1Balance || 0) + (wizardData.initialBalances?.member2Balance || 0);
        
        const completeData = {
            fundName: wizardData.fundBasics.fundName,
            abn: wizardData.fundBasics.abn,
            tfn: wizardData.fundBasics.tfn,
            trusteeType: wizardData.fundBasics?.trusteeType || 'individual',
            trustees: wizardData.trustees,
            members: wizardData.members,
            fundBalance: totalBalance,
            totalAssets: totalBalance,
            bsb: wizardData.bankDetails?.bsb,
            accountNumber: wizardData.bankDetails?.accountNumber,
            accountName: wizardData.bankDetails?.accountName,
            riskProfile: wizardData.investmentStrategy?.riskProfile,
            assetAllocation: wizardData.investmentStrategy
        };

        setSmsfState(completeData);
        if (onComplete) onComplete(completeData);
    };

    const renderStepContent = () => {
        switch(currentStep) {
            case 0:
                return renderWelcome();
            case 1:
                return renderFundBasics();
            case 2:
                return renderTrustees();
            case 3:
                return renderMembers();
            case 4:
                return renderInitialBalances();
            case 5:
                return renderBankDetails();
            case 6:
                return renderInvestmentStrategy();
            case 7:
                return renderReview();
            default:
                return null;
        }
    };

    const renderWelcome = () => (
        <div className="text-center space-y-6">
            <div className="text-6xl">👋</div>
            <h3 className="text-2xl font-bold text-gray-900">Welcome to SMSF Setup Wizard</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
                This wizard will guide you through setting up your SMSF details in 7 easy steps. 
                It takes about 10-15 minutes to complete.
            </p>
            <div className="bg-blue-50 p-6 rounded-lg max-w-xl mx-auto text-left">
                <h4 className="font-semibold text-blue-900 mb-3">What you'll need:</h4>
                <ul className="space-y-2 text-sm text-blue-800">
                    <li>✓ Fund name and ABN</li>
                    <li>✓ Trustee details (names, TFNs, DOBs)</li>
                    <li>✓ Current super balances</li>
                    <li>✓ Bank account details (BSB, account number)</li>
                    <li>✓ Investment preferences</li>
                </ul>
            </div>
            <button
                onClick={() => setCurrentStep(1)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors"
            >
                Next →
            </button>
        </div>
    );

    const renderTrustees = () => (
        <div className="space-y-4">
            <h4 className="font-semibold text-lg">Add Trustees</h4>
            <p className="text-sm text-gray-600">Enter name, TFN, and date of birth for each trustee</p>
            {[0, 1].map((index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-medium mb-3">Trustee {index + 1}</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-sm mb-1">Name</label>
                            <input
                                type="text"
                                value={wizardData.trustees[index]?.name || ''}
                                onChange={(e) => {
                                    const newTrustees = [...wizardData.trustees];
                                    newTrustees[index] = { ...newTrustees[index], name: e.target.value };
                                    setWizardData({ ...wizardData, trustees: newTrustees });
                                }}
                                className="w-full px-3 py-2 border rounded-md"
                                placeholder="John Smith"
                            />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">TFN</label>
                            <input
                                type="text"
                                maxLength="9"
                                value={wizardData.trustees[index]?.tfn || ''}
                                onChange={(e) => {
                                    const newTrustees = [...wizardData.trustees];
                                    newTrustees[index] = { ...newTrustees[index], tfn: e.target.value };
                                    setWizardData({ ...wizardData, trustees: newTrustees });
                                }}
                                className="w-full px-3 py-2 border rounded-md"
                                placeholder="123456789"
                            />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Date of Birth</label>
                            <input
                                type="date"
                                value={wizardData.trustees[index]?.dob || ''}
                                onChange={(e) => {
                                    const newTrustees = [...wizardData.trustees];
                                    newTrustees[index] = { ...newTrustees[index], dob: e.target.value };
                                    setWizardData({ ...wizardData, trustees: newTrustees });
                                }}
                                className="w-full px-3 py-2 border rounded-md"
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderInitialBalances = () => (
        <div className="space-y-4">
            <h4 className="font-semibold text-lg">Enter Starting Super Balances</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Member 1 Balance ($)</label>
                    <input
                        {...getNumericInputProps()}
                        value={wizardData.initialBalances.member1Balance || ''}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            initialBalances: { ...wizardData.initialBalances, member1Balance: Number(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="500000"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Member 2 Balance ($)</label>
                    <input
                        {...getNumericInputProps()}
                        value={wizardData.initialBalances.member2Balance || ''}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            initialBalances: { ...wizardData.initialBalances, member2Balance: Number(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="300000"
                    />
                </div>
            </div>
        </div>
    );

    const renderBankDetails = () => (
        <div className="space-y-4">
            <h4 className="font-semibold text-lg">Bank Account Details</h4>
            <p className="text-sm text-gray-600">SMSF bank account for contributions and payments</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">BSB</label>
                    <input
                        type="text"
                        maxLength="6"
                        value={wizardData.bankDetails.bsb}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            bankDetails: { ...wizardData.bankDetails, bsb: e.target.value.replace(/\D/g, '') }
                        })}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="123-456"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Account Number</label>
                    <input
                        type="text"
                        value={wizardData.bankDetails.accountNumber}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            bankDetails: { ...wizardData.bankDetails, accountNumber: e.target.value.replace(/\D/g, '') }
                        })}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="12345678"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Account Name</label>
                    <input
                        type="text"
                        value={wizardData.bankDetails.accountName}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            bankDetails: { ...wizardData.bankDetails, accountName: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="Smith Family Superannuation Fund"
                    />
                </div>
            </div>
        </div>
    );

    const renderInvestmentStrategy = () => (
        <div className="space-y-4">
            <h4 className="font-semibold text-lg">Select Risk Profile</h4>
            <p className="text-sm text-gray-600">Choose a profile and we'll pre-fill asset allocation</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['conservative', 'balanced', 'growth'].map((profile) => (
                    <button
                        key={profile}
                        onClick={() => {
                            const allocations = {
                                conservative: { ausShares: 20, intlShares: 15, property: 5, bonds: 40, cash: 20 },
                                balanced: { ausShares: 30, intlShares: 25, property: 10, bonds: 20, cash: 15 },
                                growth: { ausShares: 40, intlShares: 35, property: 15, bonds: 5, cash: 5 }
                            };
                            setWizardData({
                                ...wizardData,
                                investmentStrategy: { riskProfile: profile, ...allocations[profile] }
                            });
                        }}
                        className={`p-4 border-2 rounded-lg text-left transition ${
                            wizardData.investmentStrategy.riskProfile === profile 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-300 hover:border-blue-300'
                        }`}
                    >
                        <div className="font-semibold capitalize">{profile}</div>
                        <div className="text-sm text-gray-600 mt-1">
                            {profile === 'conservative' && 'Lower risk, lower returns'}
                            {profile === 'balanced' && 'Moderate risk, balanced returns'}
                            {profile === 'growth' && 'Higher risk, higher returns'}
                        </div>
                    </button>
                ))}
            </div>

            <div className="bg-white p-4 rounded-lg shadow mt-4">
                <h5 className="font-medium mb-3">Asset Allocation</h5>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div>
                        <label className="block mb-1">Aus Shares (%)</label>
                        <input {...getNumericInputProps()} value={wizardData.investmentStrategy.australianShares} readOnly 
                            className="w-full px-2 py-1 border rounded bg-gray-50" />
                    </div>
                    <div>
                        <label className="block mb-1">Intl Shares (%)</label>
                        <input {...getNumericInputProps()} value={wizardData.investmentStrategy.internationalShares} readOnly 
                            className="w-full px-2 py-1 border rounded bg-gray-50" />
                    </div>
                    <div>
                        <label className="block mb-1">Property (%)</label>
                        <input {...getNumericInputProps()} value={wizardData.investmentStrategy.property} readOnly 
                            className="w-full px-2 py-1 border rounded bg-gray-50" />
                    </div>
                    <div>
                        <label className="block mb-1">Bonds (%)</label>
                        <input {...getNumericInputProps()} value={wizardData.investmentStrategy.bonds} readOnly 
                            className="w-full px-2 py-1 border rounded bg-gray-50" />
                    </div>
                    <div>
                        <label className="block mb-1">Cash (%)</label>
                        <input {...getNumericInputProps()} value={wizardData.investmentStrategy.cash} readOnly 
                            className="w-full px-2 py-1 border rounded bg-gray-50" />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderFundBasics = () => (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-semibold text-blue-900 mb-2">📚 Step 1: Fund Basics</h4>
                <p className="text-sm text-blue-800 mb-3">
                    We need some fundamental information about your SMSF to get started.
                </p>
                <div className="bg-white p-3 rounded text-xs text-gray-700">
                    <strong>💡 Tutorial:</strong> Your SMSF is a private superannuation fund that you control. 
                    Every SMSF must have an Australian Business Number (ABN) and can have 1-6 members. 
                    Members can be individual trustees or you can use a corporate trustee structure.
                </div>
            </div>

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMSF Fund Name *
                    </label>
                    <input
                        type="text"
                        value={wizardData.fundBasics.fundName}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            fundBasics: { ...wizardData.fundBasics, fundName: e.target.value }
                        })}
                        placeholder="e.g., Smith Family Superannuation Fund"
                        className={`w-full px-3 py-2 border rounded-md ${
                            validation.fundName ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {validation.fundName && (
                        <p className="text-red-600 text-xs mt-1">{validation.fundName}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        ABN (Australian Business Number) *
                    </label>
                    <input
                        type="text"
                        value={wizardData.fundBasics.abn}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            fundBasics: { ...wizardData.fundBasics, abn: e.target.value.replace(/\D/g, '').slice(0, 11) }
                        })}
                        placeholder="12345678901"
                        maxLength={11}
                        className={`w-full px-3 py-2 border rounded-md ${
                            validation.abn ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {validation.abn && (
                        <p className="text-red-600 text-xs mt-1">{validation.abn}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Trustee Type *
                    </label>
                    <div className="space-y-2">
                        <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                            <input
                                type="radio"
                                name="trusteeType"
                                value="individual"
                                checked={wizardData.fundBasics.trusteeType === 'individual'}
                                onChange={(e) => setWizardData({
                                    ...wizardData,
                                    fundBasics: { ...wizardData.fundBasics, trusteeType: e.target.value }
                                })}
                                className="mr-3"
                            />
                            <div>
                                <div className="font-medium">Individual Trustees</div>
                                <div className="text-sm text-gray-600">All members are trustees (lower cost)</div>
                            </div>
                        </label>
                        <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                            <input
                                type="radio"
                                name="trusteeType"
                                value="corporate"
                                checked={wizardData.fundBasics.trusteeType === 'corporate'}
                                onChange={(e) => setWizardData({
                                    ...wizardData,
                                    fundBasics: { ...wizardData.fundBasics, trusteeType: e.target.value }
                                })}
                                className="mr-3"
                            />
                            <div>
                                <div className="font-medium">Corporate Trustee</div>
                                <div className="text-sm text-gray-600">Separate company (higher cost, better succession)</div>
                            </div>
                        </label>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Number of Members *
                        </label>
                        <input
                            {...getNumericInputProps()}
                            min="1"
                            max="6"
                            value={wizardData.fundBasics.numberOfMembers}
                            onChange={(e) => {
                                const num = Number(e.target.value);
                                const newMembers = [...wizardData.members];
                                while (newMembers.length < num) {
                                    newMembers.push({ name: '', age: 0, balance: 0, role: 'member' });
                                }
                                setWizardData({
                                    ...wizardData,
                                    fundBasics: { ...wizardData.fundBasics, numberOfMembers: num },
                                    members: newMembers.slice(0, num)
                                });
                            }}
                            className={`w-full px-3 py-2 border rounded-md ${
                                validation.numberOfMembers ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        {validation.numberOfMembers && (
                            <p className="text-red-600 text-xs mt-1">{validation.numberOfMembers}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Year Established
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={wizardData.fundBasics.establishmentYear || ''}
                            onChange={(e) => setWizardData({
                                ...wizardData,
                                fundBasics: { ...wizardData.fundBasics, establishmentYear: Number(e.target.value) || 0 }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderMembers = () => (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-semibold text-blue-900 mb-2">📚 Step 2: Member Details</h4>
                <p className="text-sm text-blue-800 mb-3">
                    We need details for each member of the SMSF.
                </p>
                <div className="bg-white p-3 rounded text-xs text-gray-700">
                    <strong>💡 Tutorial:</strong> SMSF members are the people who have their superannuation in the fund. 
                    All members must be either trustees or directors of the corporate trustee. Each member's age affects 
                    their contribution eligibility, pension access, and work test requirements.
                </div>
            </div>

            {wizardData.members.map((member, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
                    <h4 className="font-semibold mb-4">Member {index + 1}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Name *
                            </label>
                            <input
                                type="text"
                                value={member.name}
                                onChange={(e) => {
                                    const newMembers = [...wizardData.members];
                                    newMembers[index].name = e.target.value;
                                    setWizardData({ ...wizardData, members: newMembers });
                                }}
                                className={`w-full px-3 py-2 border rounded-md ${
                                    validation[`member_${index}_name`] ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Age *
                            </label>
                            <input
                                {...getNumericInputProps()}
                                value={member.age || ''}
                                onChange={(e) => {
                                    const newMembers = [...wizardData.members];
                                    newMembers[index].age = Number(e.target.value) || 0;
                                    setWizardData({ ...wizardData, members: newMembers });
                                }}
                                className={`w-full px-3 py-2 border rounded-md ${
                                    validation[`member_${index}_age`] ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Current Balance
                            </label>
                            <input
                                {...getNumericInputProps()}
                                value={member.balance || ''}
                                onChange={(e) => {
                                    const newMembers = [...wizardData.members];
                                    newMembers[index].balance = Number(e.target.value) || 0;
                                    setWizardData({ ...wizardData, members: newMembers });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderAssets = () => (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-semibold text-blue-900 mb-2">📚 Step 3: Asset Allocation</h4>
                <p className="text-sm text-blue-800 mb-3">
                    Understanding your current asset allocation helps us provide better recommendations.
                </p>
                <div className="bg-white p-3 rounded text-xs text-gray-700">
                    <strong>💡 Tutorial:</strong> SMSFs can invest in a wide range of assets including shares, property, 
                    cash, and bonds. Diversification across asset classes reduces risk. Your investment strategy should 
                    align with your risk profile and retirement timeline. The "sole purpose test" requires all investments 
                    to be for the benefit of members in retirement.
                </div>
            </div>

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Assets *
                    </label>
                    <input
                        {...getNumericInputProps()}
                        value={wizardData.assets.totalAssets || ''}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            assets: { ...wizardData.assets, totalAssets: Number(e.target.value) || 0 }
                        })}
                        className={`w-full px-3 py-2 border rounded-md text-lg font-semibold ${
                            validation.totalAssets ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {validation.totalAssets && (
                        <p className="text-red-600 text-xs mt-1">{validation.totalAssets}</p>
                    )}
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium mb-3">Asset Breakdown</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            { key: 'cash', label: 'Cash & Term Deposits', icon: '💵' },
                            { key: 'australianShares', label: 'Australian Shares', icon: '📈' },
                            { key: 'internationalShares', label: 'International Shares', icon: '🌏' },
                            { key: 'property', label: 'Property', icon: '🏠' },
                            { key: 'fixedInterest', label: 'Fixed Interest/Bonds', icon: '📊' },
                            { key: 'other', label: 'Other Assets', icon: '💼' }
                        ].map(asset => (
                            <div key={asset.key}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {asset.icon} {asset.label}
                                </label>
                                <input
                                    {...getNumericInputProps()}
                                    value={wizardData.assets[asset.key]}
                                    onChange={(e) => setWizardData({
                                        ...wizardData,
                                        assets: { ...wizardData.assets, [asset.key]: Number(e.target.value) }
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderContributions = () => (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-semibold text-blue-900 mb-2">📚 Step 4: Contributions</h4>
                <p className="text-sm text-blue-800 mb-3">
                    How much have you contributed this financial year?
                </p>
                <div className="bg-white p-3 rounded text-xs text-gray-700">
                    <strong>💡 Tutorial:</strong> There are two types of contributions: <strong>Concessional</strong> (before-tax, 
                    includes employer SG and salary sacrifice - taxed at 15%) and <strong>Non-concessional</strong> (after-tax, 
                    from your own money - not taxed). Annual caps apply: ${FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL.toLocaleString()} concessional and ${FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL.toLocaleString()} non-concessional. 
                    Exceeding these caps can result in significant penalties.
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <h4 className="font-semibold mb-4">Concessional (Before-tax)</h4>
                    <input
                        {...getNumericInputProps()}
                        value={wizardData.contributions.concessionalThisYear || ''}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            contributions: { ...wizardData.contributions, concessionalThisYear: Number(e.target.value) || 0 }
                        })}
                        className={`w-full px-3 py-2 border rounded-md ${
                            validation.concessional ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    <div className="mt-2 text-sm">
                        <div className="flex justify-between text-gray-600">
                            <span>Cap:</span>
                            <span>${FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                            <span>Remaining:</span>
                            <span className="font-semibold text-green-600">
                                ${(FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL - wizardData.contributions.concessionalThisYear).toLocaleString()}
                            </span>
                        </div>
                    </div>
                    {validation.concessional && (
                        <p className="text-red-600 text-xs mt-2">{validation.concessional}</p>
                    )}
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h4 className="font-semibold mb-4">Non-Concessional (After-tax)</h4>
                    <input
                        {...getNumericInputProps()}
                        value={wizardData.contributions.nonConcessionalThisYear || ''}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            contributions: { ...wizardData.contributions, nonConcessionalThisYear: Number(e.target.value) || 0 }
                        })}
                        className={`w-full px-3 py-2 border rounded-md ${
                            validation.nonConcessional ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    <div className="mt-2 text-sm">
                        <div className="flex justify-between text-gray-600">
                            <span>Cap:</span>
                            <span>${FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                            <span>Remaining:</span>
                            <span className="font-semibold text-green-600">
                                ${(FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL - wizardData.contributions.nonConcessionalThisYear).toLocaleString()}
                            </span>
                        </div>
                    </div>
                    {validation.nonConcessional && (
                        <p className="text-red-600 text-xs mt-2">{validation.nonConcessional}</p>
                    )}
                </div>
            </div>
        </div>
    );

    const renderGoals = () => (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-semibold text-blue-900 mb-2">📚 Step 5: Goals & Strategy</h4>
                <p className="text-sm text-blue-800 mb-3">
                    Your goals help us tailor recommendations to your needs.
                </p>
                <div className="bg-white p-3 rounded text-xs text-gray-700">
                    <strong>💡 Tutorial:</strong> Your investment strategy must align with your goals and risk tolerance. 
                    <strong>Retirement Income</strong> focuses on generating sustainable income in retirement. 
                    <strong>Wealth Accumulation</strong> aims to maximize growth before retirement. 
                    <strong>Estate Planning</strong> prioritizes preserving wealth for beneficiaries. Your risk profile 
                    determines asset allocation - conservative (30% growth assets) to aggressive (85% growth assets).
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Goal *
                </label>
                <div className="space-y-2">
                    {[
                        { value: 'retirement_income', label: 'Generate Retirement Income', desc: 'Focus on sustainable income in retirement' },
                        { value: 'wealth_accumulation', label: 'Wealth Accumulation', desc: 'Maximize growth before retirement' },
                        { value: 'estate_planning', label: 'Estate Planning', desc: 'Preserve wealth for beneficiaries' }
                    ].map(goal => (
                        <label key={goal.value} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                            <input
                                type="radio"
                                name="primaryGoal"
                                value={goal.value}
                                checked={wizardData.goals.primaryGoal === goal.value}
                                onChange={(e) => setWizardData({
                                    ...wizardData,
                                    goals: { ...wizardData.goals, primaryGoal: e.target.value }
                                })}
                                className="mr-3"
                            />
                            <div>
                                <div className="font-medium">{goal.label}</div>
                                <div className="text-sm text-gray-600">{goal.desc}</div>
                            </div>
                        </label>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Target Retirement Age
                    </label>
                    <input
                        {...getNumericInputProps()}
                        value={wizardData.goals.retirementAge}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            goals: { ...wizardData.goals, retirementAge: Number(e.target.value) }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Target Annual Retirement Income
                    </label>
                    <input
                        {...getNumericInputProps()}
                        value={wizardData.goals.targetRetirementIncome}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            goals: { ...wizardData.goals, targetRetirementIncome: Number(e.target.value) }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Risk Profile *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                        { value: 'conservative', label: 'Conservative', emoji: '🛡️' },
                        { value: 'balanced', label: 'Balanced', emoji: '⚖️' },
                        { value: 'growth', label: 'Growth', emoji: '📈' },
                        { value: 'aggressive', label: 'Aggressive', emoji: '🚀' }
                    ].map(risk => (
                        <label key={risk.value} className={`p-3 border-2 rounded-lg cursor-pointer text-center ${
                            wizardData.goals.riskProfile === risk.value 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-300 hover:border-gray-400'
                        }`}>
                            <input
                                type="radio"
                                name="riskProfile"
                                value={risk.value}
                                checked={wizardData.goals.riskProfile === risk.value}
                                onChange={(e) => setWizardData({
                                    ...wizardData,
                                    goals: { ...wizardData.goals, riskProfile: e.target.value }
                                })}
                                className="sr-only"
                            />
                            <div className="text-2xl mb-1">{risk.emoji}</div>
                            <div className="text-sm font-medium">{risk.label}</div>
                        </label>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderSpecialFeatures = () => (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-semibold text-blue-900 mb-2">📚 Step 6: Special Features</h4>
                <p className="text-sm text-blue-800 mb-3">
                    Let us know about borrowing, collectibles, or other special situations.
                </p>
                <div className="bg-white p-3 rounded text-xs text-gray-700">
                    <strong>💡 Tutorial:</strong> <strong>LRBA</strong> (Limited Recourse Borrowing) allows your SMSF to 
                    borrow for property investment (max 80% LVR). <strong>Pension phase</strong> means 0% tax on investment 
                    earnings (available from preservation age). <strong>Collectibles</strong> (art, wine) have special storage 
                    and display restrictions. <strong>Related party</strong> transactions are heavily regulated and limited to 5% of fund assets.
                </div>
            </div>

            <div className="space-y-4">
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                        type="checkbox"
                        checked={wizardData.specialFeatures.hasLRBA}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            specialFeatures: { ...wizardData.specialFeatures, hasLRBA: e.target.checked }
                        })}
                        className="mr-3"
                    />
                    <div className="flex-1">
                        <div className="font-medium">Limited Recourse Borrowing Arrangement (LRBA)</div>
                        <div className="text-sm text-gray-600">SMSF has borrowed to purchase property</div>
                    </div>
                </label>

                {wizardData.specialFeatures.hasLRBA && (
                    <div className="ml-8 p-4 bg-gray-50 rounded-lg">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Property Value</label>
                                <input
                                    {...getNumericInputProps()}
                                    value={wizardData.specialFeatures.lrbaPropertyValue}
                                    onChange={(e) => setWizardData({
                                        ...wizardData,
                                        specialFeatures: { ...wizardData.specialFeatures, lrbaPropertyValue: Number(e.target.value) }
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Loan Amount</label>
                                <input
                                    {...getNumericInputProps()}
                                    value={wizardData.specialFeatures.lrbaLoanAmount}
                                    onChange={(e) => setWizardData({
                                        ...wizardData,
                                        specialFeatures: { ...wizardData.specialFeatures, lrbaLoanAmount: Number(e.target.value) }
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        </div>
                        {validation.lrba && (
                            <p className="text-red-600 text-xs mt-2">{validation.lrba}</p>
                        )}
                    </div>
                )}

                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                        type="checkbox"
                        checked={wizardData.specialFeatures.inPensionPhase}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            specialFeatures: { ...wizardData.specialFeatures, inPensionPhase: e.target.checked }
                        })}
                        className="mr-3"
                    />
                    <div className="flex-1">
                        <div className="font-medium">Pension Phase</div>
                        <div className="text-sm text-gray-600">One or more members receiving pension payments</div>
                    </div>
                </label>

                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                        type="checkbox"
                        checked={wizardData.specialFeatures.hasCollectibles}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            specialFeatures: { ...wizardData.specialFeatures, hasCollectibles: e.target.checked }
                        })}
                        className="mr-3"
                    />
                    <div className="flex-1">
                        <div className="font-medium">Collectibles</div>
                        <div className="text-sm text-gray-600">Art, wine, or other collectible assets</div>
                    </div>
                </label>

                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                        type="checkbox"
                        checked={wizardData.specialFeatures.hasRelatedPartyTransactions}
                        onChange={(e) => setWizardData({
                            ...wizardData,
                            specialFeatures: { ...wizardData.specialFeatures, hasRelatedPartyTransactions: e.target.checked }
                        })}
                        className="mr-3"
                    />
                    <div className="flex-1">
                        <div className="font-medium">Related Party Transactions</div>
                        <div className="text-sm text-gray-600">Transactions with fund members or related parties</div>
                    </div>
                </label>
            </div>
        </div>
    );

    const renderReview = () => (
        <div className="space-y-6">
            {/* Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">📋 Your SMSF Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Fund Details</h4>
                        <div className="space-y-1 text-sm">
                            <div><span className="text-gray-600">Name:</span> <span className="font-medium">{wizardData.fundBasics.fundName}</span></div>
                            <div><span className="text-gray-600">ABN:</span> <span className="font-medium">{wizardData.fundBasics.abn}</span></div>
                            <div><span className="text-gray-600">Trustee:</span> <span className="font-medium capitalize">{wizardData.fundBasics.trusteeType}</span></div>
                            <div><span className="text-gray-600">Members:</span> <span className="font-medium">{wizardData.fundBasics.numberOfMembers}</span></div>
                        </div>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Balances & Strategy</h4>
                        <div className="space-y-1 text-sm">
                            <div><span className="text-gray-600">Total Balance:</span> <span className="font-medium">${((wizardData.initialBalances?.member1Balance || 0) + (wizardData.initialBalances?.member2Balance || 0)).toLocaleString()}</span></div>
                            <div><span className="text-gray-600">Risk Profile:</span> <span className="font-medium capitalize">{wizardData.investmentStrategy?.riskProfile || 'Balanced'}</span></div>
                            <div><span className="text-gray-600">Bank:</span> <span className="font-medium">{wizardData.bankDetails?.bsb ? `BSB ${wizardData.bankDetails.bsb}` : 'Not provided'}</span></div>
                        </div>
                    </div>
                </div>
            </div>

            <CalculationTransparency
                kind="smsf"
                title="SMSF Setup Transparency"
                inputs={{
                    fundName: wizardData.fundBasics?.fundName,
                    memberCount: wizardData.members?.length,
                    totalBalance: (Number(wizardData.initialBalances?.member1Balance) || 0) + (Number(wizardData.initialBalances?.member2Balance) || 0),
                }}
                results={{ recommendations }}
            />

            {/* Recommendations */}
            {recommendations.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">💡 Personalized Recommendations</h3>
                    <div className="space-y-4">
                        {recommendations.map((rec, index) => (
                            <div key={index} className={`border-l-4 p-4 rounded ${
                                rec.priority === 'high' ? 'bg-red-50 border-red-500' :
                                rec.priority === 'medium' ? 'bg-yellow-50 border-yellow-500' :
                                'bg-blue-50 border-blue-500'
                            }`}>
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <span className="text-xs font-medium text-gray-500 uppercase">{rec.category}</span>
                                        <h4 className="font-semibold text-gray-900">{rec.title}</h4>
                                    </div>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        rec.priority === 'high' ? 'bg-red-100 text-red-800' :
                                        rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-blue-100 text-blue-800'
                                    }`}>
                                        {rec.priority.toUpperCase()}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-700 mb-2">{rec.message}</p>
                                <p className="text-sm font-medium text-gray-900">→ {rec.action}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Suggested Calculators */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">🎯 Suggested Calculators for You</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getSuggestedCalculators().map((calc, index) => (
                        <div key={index} className="border rounded-lg p-4 hover:shadow-md transition">
                            <div className="text-2xl mb-2">{calc.icon}</div>
                            <h4 className="font-semibold mb-1">{calc.name}</h4>
                            <p className="text-sm text-gray-600">{calc.reason}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Complete Button */}
            <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-green-900 mb-2">Ready to complete setup!</h4>
                <p className="text-sm text-green-800 mb-4">
                    We'll apply all your settings and take you to the main SMSF calculator.
                </p>
                <button
                    onClick={completeWizard}
                    className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 font-semibold"
                >
                    Complete Setup & Start Calculating
                </button>
            </div>
        </div>
    );

    const getSuggestedCalculators = () => {
        const suggestions = [];
        const totalBalance = (wizardData.initialBalances?.member1Balance || 0) + (wizardData.initialBalances?.member2Balance || 0);

        // Based on age
        const members = wizardData.members || [];
        const ages = members.map(m => m.age || 0).filter(a => a > 0);
        if (ages.length > 0) {
            const avgAge = ages.reduce((sum, a) => sum + a, 0) / ages.length;
            const oldestAge = Math.max(...ages);
            
            if (oldestAge >= 60) {
                suggestions.push({
                    icon: '💰',
                    name: 'Pension Optimizer',
                    reason: 'Members are pension-eligible - explore tax-free pension phase'
                });
            }
            
            // SMSF vs Industry comparison
            if (avgAge < 50 || totalBalance < 300000) {
                suggestions.push({
                    icon: '⚖️',
                    name: 'SMSF vs Industry Fund',
                    reason: 'Compare if SMSF is the right choice for your situation'
                });
            }
        }

        // General useful calculators
        suggestions.push({
            icon: '📅',
            name: 'Carry-Forward Calculator',
            reason: 'Maximize unused contribution caps from previous years'
        });
        
        suggestions.push({
            icon: '✅',
            name: 'Compliance Dashboard',
            reason: 'Monitor your SMSF compliance status'
        });

        // Always relevant
        suggestions.push({
            icon: '📄',
            name: 'Reporting Center',
            reason: 'Generate comprehensive reports and documentation'
        });

        suggestions.push({
            icon: '📧',
            name: 'Email Automation',
            reason: 'Set up automated report delivery to trustees and advisors'
        });

        return suggestions.slice(0, 6); // Top 6 suggestions
    };

    const renderProgressBar = () => (
        <div className="mb-8">
            <div className="flex justify-between mb-2">
                {steps.map((step, index) => (
                    <div key={step.id} className="flex-1 relative">
                        <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                                index < currentStep ? 'bg-green-500 text-white' :
                                index === currentStep ? 'bg-blue-500 text-white' :
                                'bg-gray-300 text-gray-600'
                            }`}>
                                {index < currentStep ? '✓' : index + 1}
                            </div>
                            <div className="text-xs mt-1 text-center hidden md:block">
                                {step.title}
                            </div>
                        </div>
                        {index < steps.length - 1 && (
                            <div className={`absolute top-5 left-1/2 w-full h-0.5 ${
                                index < currentStep ? 'bg-green-500' : 'bg-gray-300'
                            }`} style={{ zIndex: -1 }} />
                        )}
                    </div>
                ))}
            </div>
            <div className="text-center mt-4">
                <h3 className="text-xl font-semibold text-gray-900">{steps[currentStep].title}</h3>
                <p className="text-sm text-gray-600">{steps[currentStep].description}</p>
            </div>
        </div>
    );

    return (
        <div className="max-w-5xl mx-auto p-6">
            {/* Header */}
            <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">🧙‍♂️ SMSF Setup Wizard & Tutorial</h2>
                <p className="text-gray-600 mb-2">
                    Learn about SMSFs while setting up your calculator in 7 easy steps
                </p>
                <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full text-sm text-blue-800">
                    <span className="mr-2">💡</span>
                    Each step includes helpful tutorials and tips
                </div>
            </div>

            {/* Progress Bar */}
            {renderProgressBar()}

            {/* Step Content */}
            <div className="bg-white rounded-lg shadow-lg p-8 min-h-96">
                {renderStepContent()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
                <button
                    onClick={prevStep}
                    disabled={currentStep === 0}
                    className={`px-6 py-3 rounded-lg font-semibold ${
                        currentStep === 0
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gray-500 text-white hover:bg-gray-600'
                    }`}
                >
                    ← Previous
                </button>

                {currentStep < steps.length - 1 ? (
                    <button
                        onClick={nextStep}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                    >
                        Next →
                    </button>
                ) : (
                    <button
                        onClick={completeWizard}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                    >
                        Complete Setup ✓
                    </button>
                )}
            </div>

            {/* Step Indicator (Mobile) */}
            <div className="text-center mt-4 text-sm text-gray-600 md:hidden">
                Step {currentStep + 1} of {steps.length}
            </div>
        </div>
    );
};

SMSFCalculatorWizard.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired,
    onComplete: PropTypes.func
};

export default SMSFCalculatorWizard;

